import React, { useState, useRef, useEffect, Component } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useAuth } from './components/AuthProvider';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';

// Error Boundary to catch crashes and show helpful message
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('App crashed:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg p-6 max-w-md text-center">
            <h1 className="text-xl font-bold text-red-400 mb-2">App Error</h1>
            <p className="text-slate-300 mb-4">Something went wrong. Try clearing your browser data:</p>
            <button 
              onClick={() => {
                // Only clear error-related state, NEVER wipe localStorage (autosaved notes live there)
                sessionStorage.clear();
                window.location.reload();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Reload App
            </button>
            <p className="text-slate-500 text-xs mt-4">Error: {this.state.error?.message}</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth Callback Handler
const AuthCallback = () => {
  const { handleGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);
  const [authError, setAuthError] = useState(false);
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const hash = window.location.hash;
    const sessionId = hash.match(/session_id=([^&]*)/)?.[1];
    
    if (!sessionId) {
      console.error('No session_id found in auth callback');
      setAuthError(true);
      setTimeout(() => {
        window.location.href = window.location.origin;
      }, 2000);
      return;
    }
    
    const timeout = setTimeout(() => {
      console.error('Auth callback timeout');
      setAuthError(true);
      toast.error('Sign-in timed out. Please try again.');
      setTimeout(() => {
        window.history.replaceState(null, '', window.location.pathname);
        window.location.href = window.location.origin;
      }, 2000);
    }, 15000);
    
    handleGoogleCallback(sessionId)
      .then(() => {
        clearTimeout(timeout);
        window.history.replaceState(null, '', window.location.pathname);
        window.location.reload();
      })
      .catch((error) => {
        clearTimeout(timeout);
        console.error('Google auth error:', error);
        toast.error('Google sign-in failed. Please try again.');
        setAuthError(true);
        setTimeout(() => {
          window.history.replaceState(null, '', window.location.pathname);
          window.location.href = window.location.origin;
        }, 2000);
      });
  }, [handleGoogleCallback]);
  
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-4 text-destructive" />
          <p className="text-foreground font-medium">Sign-in failed</p>
          <p className="text-muted-foreground text-sm mt-2">Redirecting to home page...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">Signing you in...</p>
        <p className="text-xs text-muted-foreground mt-2">If this takes too long, you'll be redirected automatically.</p>
      </div>
    </div>
  );
};

// Lazy load wrappers
const SessionViewerWrapper = () => {
  const SessionViewer = React.lazy(() => import('./components/SessionViewer'));
  return (
    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
      <SessionViewer />
    </React.Suspense>
  );
};

const HistoryViewerWrapper = () => {
  const HistoryViewer = React.lazy(() => import('./components/HistoryViewer'));
  return (
    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
      <HistoryViewer />
    </React.Suspense>
  );
};

const TeamDashboardWrapper = () => {
  const TeamDashboard = React.lazy(() => import('./components/TeamDashboard'));
  return (
    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
      <TeamDashboard />
    </React.Suspense>
  );
};

const TeamMemberViewWrapper = () => {
  const TeamMemberView = React.lazy(() => import('./components/TeamMemberView'));
  return (
    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
      <TeamMemberView />
    </React.Suspense>
  );
};

const ProviderDashboardWrapper = () => {
  const ProviderDashboard = React.lazy(() => import('./components/ProviderDashboard'));
  return (
    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
      <ProviderDashboard />
    </React.Suspense>
  );
};

const AdminPortalWrapper = () => {
  const AdminPortal = React.lazy(() => import('./components/AdminPortal'));
  return (
    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
      <AdminPortal />
    </React.Suspense>
  );
};

const DriveHomeModeWrapper = () => {
  const DriveHomeMode = React.lazy(() => import('./components/mediscribe/DriveHomeMode'));
  return (
    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
      <DriveHomeMode />
    </React.Suspense>
  );
};

// Main Router
const AppRouter = ({ MediScribe }) => {
  const location = useLocation();
  
  // EMERGENCY RESET: If URL has ?reset=1, clear all data and redirect
  if (location.search?.includes('reset=1')) {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(";").forEach((c) => {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    window.location.href = window.location.origin;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-foreground">Clearing data and reloading...</p>
        </div>
      </div>
    );
  }
  
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<MediScribe />} />
      <Route path="/dashboard" element={<ProviderDashboardWrapper />} />
      <Route path="/admin" element={<AdminPortalWrapper />} />
      <Route path="/view/:shareCode" element={<SessionViewerWrapper />} />
      <Route path="/history/:shareCode" element={<HistoryViewerWrapper />} />
      <Route path="/team/:providerId" element={<TeamDashboardWrapper />} />
      <Route path="/team/view/:memberId" element={<TeamMemberViewWrapper />} />
      <Route path="/drive" element={<DriveHomeModeWrapper />} />
    </Routes>
  );
};

export default AppRouter;
